import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE as P } from "../tokens";
import { authApi } from "../api/auth";
import { Wordmark } from "../components/Wordmark";

/**
 * ResetPasswordPage — lands here when the user clicks the reset link
 * from their email. Supabase Auth appends the recovery token to the
 * URL FRAGMENT (#access_token=...&refresh_token=...&type=recovery),
 * not the query string. That's why we read from window.location.hash
 * rather than useSearchParams.
 *
 * On success, redirect to /login with a one-shot flash message.
 * On expired/invalid token, render the standard error copy with a
 * prompt to request a fresh link.
 */
function parseHashTokens() {
  if (typeof window === "undefined") return { accessToken: "", type: "" };
  const raw = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(raw);
  return {
    accessToken: params.get("access_token") || "",
    type: params.get("type") || "",
    errorDescription: params.get("error_description") || "",
  };
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useState("");
  const [linkError, setLinkError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const { accessToken, type, errorDescription } = parseHashTokens();
    // Supabase puts errors in the fragment too (e.g. "Email link is
    // invalid or has expired"). Treat any of: no token, wrong type,
    // or explicit error_description as "link is dead".
    if (errorDescription || !accessToken || (type && type !== "recovery")) {
      setLinkError("This link has expired. Request a new one.");
      return;
    }
    setAccessToken(accessToken);
    // Strip the hash from the URL so the token doesn't sit in browser
    // history / any shared screenshots.
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError("");

    if (password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setFormError("Passwords don\u2019t match.");
      return;
    }

    setSubmitting(true);
    try {
      await authApi.resetPassword(accessToken, password);
      navigate("/login", {
        replace: true,
        state: { flash: "Password updated. Sign in." },
      });
    } catch (err) {
      // Backend always returns 400 with a specific message for dead
      // links / weak passwords. Surface what it said; fall back to
      // the "expired" copy for anything unclassified.
      const msg = err?.message || "This link has expired. Request a new one.";
      if (err?.status === 400 && msg.toLowerCase().includes("expired")) {
        setLinkError(msg);
      } else {
        setFormError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <style>{`
        .rp-root {
          font-family: 'DM Sans', sans-serif;
          background: ${P.bg};
          min-height: 100vh;
          color: ${P.text};
          max-width: 420px;
          margin: 0 auto;
          padding: 64px 28px 48px;
        }
        .rp-input {
          width: 100%;
          background: ${P.surface};
          border: 1.5px solid ${P.border};
          border-radius: 14px;
          padding: 14px 18px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px; font-weight: 300;
          color: ${P.text};
          outline: none;
          transition: border-color 0.2s;
        }
        .rp-input:focus { border-color: ${P.primaryMid}; }
        .rp-btn {
          width: 100%; padding: 17px;
          background: ${P.dark}; border: none; border-radius: 16px;
          cursor: pointer;
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 18px; font-style: italic; color: ${P.bg};
          letter-spacing: 0.01em;
          transition: opacity 0.15s;
        }
        .rp-btn:disabled { opacity: 0.45; cursor: default; }
      `}</style>
      <div className="rp-root">
        <Wordmark style={{ marginBottom: 24 }} />

        {linkError ? (
          <>
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 34, fontStyle: "italic", fontWeight: 400,
              color: P.text, lineHeight: 1.12, marginBottom: 16,
            }}>
              Link expired.
            </h1>
            <p style={{
              fontSize: 14, color: P.textMid, fontWeight: 300,
              lineHeight: 1.7, marginBottom: 24,
            }}>
              {linkError}
            </p>
            <button
              className="rp-btn"
              onClick={() => navigate("/login", { replace: true })}
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 34, fontStyle: "italic", fontWeight: 400,
              color: P.text, lineHeight: 1.12, marginBottom: 10,
            }}>
              Set a new<br />password.
            </h1>
            <p style={{
              fontSize: 13.5, color: P.textMute, fontWeight: 300,
              lineHeight: 1.65, marginBottom: 28,
            }}>
              Eight characters or more. You&rsquo;ll be signed in on the
              next screen.
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 7 }}>
                  New password
                </div>
                <input
                  className="rp-input"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 7 }}>
                  Confirm password
                </div>
                <input
                  className="rp-input"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                />
              </div>

              {formError && (
                <div style={{
                  padding: "12px 16px", borderRadius: 12,
                  background: "rgba(158,132,86,0.08)",
                  marginBottom: 16,
                }}>
                  <p style={{ fontSize: 13, color: P.primary, fontWeight: 300 }}>
                    {formError}
                  </p>
                </div>
              )}

              <button
                className="rp-btn"
                type="submit"
                disabled={submitting || !password || !confirm}
              >
                {submitting ? "Updating\u2026" : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </>
  );
}
